import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format } from 'date-fns';

interface ChartMetadata {
  title: string;
  description?: string;
  dateRange?: string;
  additionalInfo?: Record<string, string>;
}

interface ChartExportButtonProps {
  chartId: string;
  filename: string;
  metadata: ChartMetadata;
  size?: 'default' | 'sm' | 'lg' | 'icon';
  variant?: 'default' | 'outline' | 'ghost';
}

export function ChartExportButton({
  chartId,
  filename,
  metadata,
  size = 'icon',
  variant = 'ghost',
}: ChartExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const chartElement = document.getElementById(chartId);

      if (!chartElement) {
        console.error(`Chart element with ID ${chartId} not found`);
        return;
      }

      // Prepare sanitized, formatted filename
      const timestamp = format(new Date(), 'yyyyMMdd-HHmmss');
      const sanitizedFilename = filename.replace(/[^a-z0-9-_]/gi, '-').toLowerCase();
      const pdfFilename = `${sanitizedFilename}_${timestamp}.pdf`;
      const pngFilename = `${sanitizedFilename}_${timestamp}.png`;

      // First create a canvas from the chart
      const canvas = await html2canvas(chartElement, {
        backgroundColor: null,
        scale: 2, // Better resolution
      });

      // Create PDF with more space for metadata
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
      });

      // Calculate dimensions
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Calculate image dimensions to fit on page with room for metadata
      const imageWidth = pageWidth - 20; // 10mm margins on each side
      const imageHeight = (canvasHeight * imageWidth) / canvasWidth;

      // Add title and metadata to the top of the page
      pdf.setFontSize(16);
      pdf.text(metadata.title, 10, 10);

      // Add description
      pdf.setFontSize(10);
      if (metadata.description) {
        pdf.text(metadata.description, 10, 20);
      }

      // Add date range
      if (metadata.dateRange) {
        pdf.text(`Date Range: ${metadata.dateRange}`, 10, 25);
      }

      // Add image of the chart
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 30, imageWidth, imageHeight);

      // Start y position for additional info after the chart
      let yPosition = 35 + imageHeight;

      // Add additional metadata info after the chart
      if (metadata.additionalInfo) {
        pdf.setFontSize(10);
        pdf.text('Additional Information:', 10, yPosition);
        yPosition += 5;

        Object.entries(metadata.additionalInfo).forEach(([key, value]) => {
          // Check if we need to add a new page
          if (yPosition > pageHeight - 10) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.text(`${key}: ${value}`, 10, yPosition);
          yPosition += 5;
        });
      }

      // Add export timestamp at the bottom
      pdf.setFontSize(8);
      pdf.text(`Generated: ${format(new Date(), 'PPP pp')}`, 10, pageHeight - 5);

      // Save the PDF
      pdf.save(pdfFilename);

      // Also save as image (PNG) with just the chart
      const link = document.createElement('a');
      link.download = pngFilename;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error exporting chart:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleExport}
      disabled={isExporting}
      aria-label="Export chart"
      title="Export chart as image and PDF with metadata"
    >
      <Download className={size === 'icon' ? 'h-4 w-4' : 'mr-2 h-4 w-4'} />
      {size !== 'icon' && (isExporting ? 'Exporting...' : 'Export')}
    </Button>
  );
}
